/**
 * P2-13 · The PLP page URL contract. Numbered pagination (the approved
 * P2-DB02 r2 model — load-more is out) over the P2-10 offset query. `page=1`
 * is dropped for canonical links; other params are preserved.
 */

/** Parse `page=` to a 1-based integer, defaulting to 1 on anything invalid. */
export function parsePageParam(raw: string | undefined): number {
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 ? n : 1;
}

/** Total pages for a result count (0 when there are no products). */
export function pageCount(total: number, pageSize: number): number {
  if (total <= 0 || pageSize <= 0) return 0;
  return Math.ceil(total / pageSize);
}

/** Clamp a requested page into `[1, totalPages]` (a deep link past the end). */
export function clampPage(page: number, totalPages: number): number {
  if (totalPages <= 0) return 1;
  return Math.min(Math.max(1, page), totalPages);
}

/** Write a page number to the query (drop page 1), preserving other params. */
export function setPageParam(search: string, page: number): string {
  const params = new URLSearchParams(search);
  if (page <= 1) params.delete("page");
  else params.set("page", String(page));
  return params.toString();
}

/**
 * The pager's item sequence with truncation: always the first/last
 * `boundaries` pages and `siblings` either side of the current one, with
 * `"ellipsis"` markers filling the gaps.
 */
export function paginationItems(
  current: number,
  total: number,
  opts: { siblings?: number; boundaries?: number } = {},
): (number | "ellipsis")[] {
  const siblings = opts.siblings ?? 1;
  const boundaries = opts.boundaries ?? 1;
  if (total <= 0) return [];

  const pages = new Set<number>();
  for (let i = 1; i <= Math.min(boundaries, total); i++) pages.add(i);
  for (let i = Math.max(1, total - boundaries + 1); i <= total; i++) pages.add(i);
  for (
    let i = Math.max(1, current - siblings);
    i <= Math.min(total, current + siblings);
    i++
  ) {
    pages.add(i);
  }

  const items: (number | "ellipsis")[] = [];
  let prev = 0;
  for (const p of [...pages].sort((a, b) => a - b)) {
    if (p - prev > 1) items.push("ellipsis");
    items.push(p);
    prev = p;
  }
  return items;
}
